import { observer } from 'mobx-react-lite'
/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
import { GearSixIcon, GitForkIcon, PlusIcon, SquaresFourIcon } from '@phosphor-icons/react'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from 'app/components/ui/sheet'
import { TemplateGallery } from './TemplateGallery'
import { useEscapeListener } from 'app/hooks/useEscapeListener'
import { useMemo, useReducer, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from 'urql'
import type { Workflow } from 'app/generated/workflows'
import { useConfirmation } from 'app/components/ConfirmationContext'
import { Button } from 'app/components/ui/button'
import { PageScroll } from 'app/components/layout/PageScroll'
import { PageHeader } from 'app/components/PageHeader'
import SimpleTable from 'app/components/table/SimpleTable'
import { type WorkflowRow, workflowColumns } from 'app/table-columns/workflows'
import { notify, notifySuccess } from 'app/services/notify.client'
import { useUser } from 'app/utils'
import { useWorkspaceStore } from 'app/stores/StoreContext'
import { logError } from 'lib/logging'
import { parseJsonField } from 'app/lib/workflows/deserialize'
import type { GateLocation } from 'app/types/workflow-gates'
import { useGateToggles } from 'app/hooks/useGateToggles'
import { GlobalSettingsModal } from './GlobalSettingsModal'
import { throttleSummary, workspaceInputFromSettings } from 'app/lib/workflows/globalSettings'
import { useWorkspaceThrottle } from './useWorkspaceThrottle'
import { LIST_WORKFLOWS_QUERY, WORKFLOW_LOCATIONS_QUERY } from 'app/lib/workflows/graphql'
import { engineEventLabel } from 'app/lib/workflows/triggerCatalog'
import { deleteRemoteWorkflow } from 'app/lib/workflows/workflowApi'
import { type WorkflowDoc, listWorkflows, removeWorkflow } from 'app/lib/workflows/workflowStore'
import { isSystemEnrolmentNodes, isSystemEnrolmentWorkflow } from 'app/lib/workflows/systemEnrolment'

/** The table owns the row shape (app/table-columns/workflows), same as every other list page. */
type WorkflowSummary = Omit<WorkflowRow, `deletable`>

const asDate = (ms: number) =>
  new Date(ms).toLocaleDateString(`en-US`, { month: `short`, day: `numeric`, year: `numeric` })
/** Location id to display name. */
type LocationNames = ReadonlyMap<string, string>

function remoteStatus(remote: Workflow): WorkflowRow[`status`] {
  // A hold pinned to a step is the engine's (a step broke); a pause by staff has a reason but no step.
  if (remote.paused) return remote.pausedNodeId ? `error` : `paused`
  if (remote.active) return `live`
  return parseJsonField<{ wentLive?: boolean }>(remote.builderJson)?.wentLive ? `off` : `draft`
}

function remoteToSummary(remote: Workflow, locationNames: LocationNames): WorkflowSummary {
  const trigger = parseJsonField<{ event?: string }>(remote.triggerJson)
  const updatedAt = Date.parse(remote.updatedDate) || 0
  const createdAt = Date.parse(remote.createdDate) || 0
  return {
    id: remote.id,
    name: remote.name || `Untitled workflow`,
    status: remoteStatus(remote),
    trigger: engineEventLabel(trigger?.event ?? `contact_created`),
    locationId: remote.locationId ?? undefined,
    location: (remote.locationId && locationNames.get(remote.locationId)) || ``,
    updated: asDate(updatedAt),
    updatedAt,
    // 0 means unknown and renders blank; "Jan 1, 1970" would be a claim.
    created: createdAt ? asDate(createdAt) : ``,
    createdAt,
    runsWorkflowId: remote.id,
    system: isSystemEnrolmentWorkflow(remote),
  }
}

/**
 * The list's rows: local drafts (the canvas's source of truth) first, then the server's workflows
 * that no local draft already represents.
 *
 * Pure and exported so the merge rules have coverage without mounting the page — the two that bit
 * in production are a local draft masking the server's `active` flag, and two local drafts claiming
 * one server workflow.
 */
export function mergeWorkflowRows(
  localDocs: WorkflowDoc[],
  hits: Workflow[],
  canSeeEnrolment: boolean,
  locationNames: LocationNames = new Map(),
  workspaceId?: string,
): { rows: WorkflowRow[]; localById: Map<string, WorkflowDoc>; remoteIds: Set<string> } {
  // The Action Center's enrolment workflows are ours, not the practice's:
  // they write system-pipeline memberships that decide who its own boards
  // find, so a practice editing one would silently change its own queues.
  // Hidden outright for everybody except a super.
  const visibleHits = hits.filter((workflow) => canSeeEnrolment || !isSystemEnrolmentWorkflow(workflow))
  const remoteById = new Map(visibleHits.map((workflow) => [workflow.id, workflow]))

  // Drafts live in per-USER storage, so every workspace the user has worked in is in there. The
  // server's list is workspace-scoped and decides for anything it has seen; a draft it has never
  // seen goes by the workspace it was saved in, or for older drafts, whether its location is here.
  const inWorkspace = (doc: WorkflowDoc) => {
    if (doc.remoteId) return remoteById.has(doc.remoteId)
    if (doc.workspaceId) return doc.workspaceId === workspaceId
    return !doc.locationId || locationNames.has(doc.locationId)
  }
  // Local drafts get the same gate as the server's, so a system workflow can't stay visible to a
  // practice simply because it hasn't synced yet.
  const allLocal = localDocs.filter(
    (doc) => inWorkspace(doc) && (canSeeEnrolment || !isSystemEnrolmentNodes(doc.nodes)),
  )
  // One row per server workflow. Two local drafts can point at the SAME `remoteId` — one keyed by
  // its original local id and one keyed by the server id, which is what a save after a
  // delete-and-recreate leaves behind. Both used to render, because the dedup below only hides a
  // REMOTE row that a local draft claims and never compared local drafts with each other.
  // `listWorkflows` returns newest first, so the first sighting is the freshest.
  const claimed = new Set<string>()
  const local = allLocal.filter((doc) => {
    if (!doc.remoteId) return true
    if (claimed.has(doc.remoteId)) return false
    claimed.add(doc.remoteId)
    return true
  })
  const saved: WorkflowSummary[] = local.map((doc) => {
    // The server owns whether a workflow is running. `doc.active` is only a copy taken when the
    // builder last saved, so anything that flipped it since — activating from another session, or
    // through the API — left this reading "Draft" over a workflow that was live and enrolling
    // people. Fall back to the local flag only for a draft the server has never seen.
    const server = doc.remoteId ? remoteById.get(doc.remoteId) : undefined
    // Same ownership as `status`: the server knows when a workflow was created; the draft only knows
    // when this browser first saved it, which is only the truth for a draft the server has never seen.
    // `||`, not `??`: Date.parse yields NaN for a missing or bad date, and NaN is not nullish.
    const createdAt = Date.parse(server?.createdDate ?? ``) || doc.createdAt || 0
    return {
      id: doc.id,
      name: doc.name || `Untitled workflow`,
      status: server ? remoteStatus(server) : doc.active ? `live` : doc.wentLive ? `off` : `draft`,
      trigger: doc.trigger,
      locationId: doc.locationId,
      location: (doc.locationId && locationNames.get(doc.locationId)) || ``,
      updated: asDate(doc.updatedAt),
      updatedAt: doc.updatedAt,
      created: createdAt ? asDate(createdAt) : ``,
      createdAt,
      // Runs are keyed by the SERVER id; the local doc id would query zero runs forever.
      runsWorkflowId: doc.remoteId,
      // A draft seeded from a system template is already ours before it ever reaches the server.
      system: isSystemEnrolmentNodes(doc.nodes),
    }
  })
  const localRemoteIds = new Set(local.map((doc) => doc.remoteId).filter(Boolean))
  const localIds = new Set(local.map((doc) => doc.id))
  const remote = visibleHits
    .filter((workflow) => !localRemoteIds.has(workflow.id) && !localIds.has(workflow.id))
    .map((workflow) => remoteToSummary(workflow, locationNames))

  // Only real workflows are deletable (local drafts + backend-only); seed samples aren't.
  const byId = new Map(local.map((doc) => [doc.id, doc]))
  const ids = new Set(visibleHits.map((workflow) => workflow.id))
  const rows: WorkflowRow[] = [...saved, ...remote].map((workflow) => ({
    ...workflow,
    deletable: byId.has(workflow.id) || ids.has(workflow.id),
  }))
  return { rows, localById: byId, remoteIds: ids }
}

/** Index page: the list of saved workflows. Entry point for the Workflows nav item. */
export const WorkflowsListPage = observer(function WorkflowsListPage() {
  const navigate = useNavigate()
  const user = useUser()
  const userId = user?.id
  const canSeeEnrolment = user?.type === `super`
  const [confirm] = useConfirmation()
  const [localVersion, bumpLocal] = useReducer((x: number) => x + 1, 0)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [templatesOpen, setTemplatesOpen] = useState(false)
  // Escape goes through the shared stack, the same pairing RadixModal uses: Radix's own capture-phase
  // Escape is suppressed on the SheetContent below, and this listener closes the sheet only when it
  // is the top of the stack. The gallery's system-templates sub-screen registers above it while
  // open, so one Escape backs out the sub-screen and the next closes the sheet.
  useEscapeListener(() => setTemplatesOpen(false), templatesOpen)
  const workspaceStore = useWorkspaceStore()
  const { workspaceId, settings: globalSettings, setSettings: setGlobalSettings } = useWorkspaceThrottle()
  const [result, reexecute] = useQuery<{ workflows: { hits: Workflow[] } }>({
    query: LIST_WORKFLOWS_QUERY,
    variables: { pagination: { limit: 100 }, sort: { field: `updatedDate`, direction: `DESC` } },
  })
  // The same query the builder's toolbar reads, so the list and the picker agree on which
  // locations exist and what they are called.
  const [locationsResult] = useQuery<{ locations: { hits: { id: string; name?: string | null }[] } }>({
    query: WORKFLOW_LOCATIONS_QUERY,
    variables: { pagination: { limit: 100 } },
  })
  const locations = locationsResult.data?.locations.hits
  const { schedulingAgent, paperwork } = useGateToggles()
  // Undefined until the locations load, so nothing is padlocked on a guess.
  const templateGating = useMemo(
    () => (locations ? { locations: locations as GateLocation[], toggles: { schedulingAgent, paperwork } } : undefined),
    [locations, schedulingAgent, paperwork],
  )
  const locationNames = useMemo(
    () => new Map((locations ?? []).map((location) => [location.id, location.name || location.id])),
    [locations],
  )

  // Local drafts (canvas source of truth) first, then backend workflows not already represented
  // locally. Dedup so a synced workflow isn't listed twice.
  //
  // Memoised deliberately: SimpleTable copies `data` into its own state whenever the array identity
  // changes, so a fresh array on every render sets state on every render and spins forever.
  // `localVersion` is what makes a local draft edit or delete recompute this.
  const { rows, localById, remoteIds } = useMemo(
    () =>
      mergeWorkflowRows(
        listWorkflows(userId),
        result.data?.workflows.hits ?? [],
        canSeeEnrolment,
        locationNames,
        workspaceId,
      ),
    [userId, result.data, localVersion, canSeeEnrolment, locationNames, workspaceId],
  )

  const handleDelete = (id: string, name: string) => {
    confirm({
      title: `Delete workflow`,
      description: `Delete “${name}”? This cancels any active runs and can't be undone.`,
      variant: `danger`,
    })
      .then(async () => {
        const doc = localById.get(id)
        const remoteId = doc?.remoteId ?? (remoteIds.has(id) ? id : undefined)
        // Delete on the server first; only drop the local copy + report success if it actually deleted.
        if (remoteId) {
          const { error } = await deleteRemoteWorkflow(remoteId)
          if (error) {
            notify(`Couldn't delete “${name}”: ${error}`)
            return
          }
        }
        if (doc) removeWorkflow(userId, id)
        notifySuccess(`Workflow deleted`)
        reexecute({ requestPolicy: `network-only` })
        bumpLocal()
      })
      .catch(() => {})
  }

  // Columns are built once so the table doesn't reset its ordering on every render. They call
  // through a ref rather than closing over the first render's handleDelete, which was made before
  // the list loaded and would resolve every workflow to "no remote id" and skip the server delete.
  const deleteRef = useRef(handleDelete)
  deleteRef.current = handleDelete
  // The one thing that rebuilds them: learning the workspace has more than one location, which is
  // when the Location column earns a place (the builder's toolbar picker follows the same rule).
  const showLocation = (locations?.length ?? 0) > 1
  const columns = useMemo(
    () => workflowColumns((id, name) => deleteRef.current(id, name), { showLocation }),
    [showLocation],
  )
  const tableMeta = useMemo(() => ({ onRowClick: (row: WorkflowRow) => navigate(`/workflows/${row.id}`) }), [navigate])
  const pickTemplate = (template: { id: string }) => navigate(`/workflows/new?template=${template.id}`)
  // Deployed workflows are created server-side without a local draft, so the document cache's copy of
  // this list knows nothing about them: refetch, or the page keeps saying "No workflows yet".
  const afterDeploy = () => {
    setTemplatesOpen(false)
    reexecute({ requestPolicy: `network-only` })
  }

  return (
    <PageScroll
      variant="table"
      header={
        <PageHeader
          title="Workflows"
          otherButtons={[
            ...(throttleSummary(globalSettings)
              ? [
                  <span
                    key="throttle"
                    className="rounded-full bg-surface-muted px-2.5 py-1 text-[11px] font-medium text-subtle"
                  >
                    {throttleSummary(globalSettings)}
                  </span>,
                ]
              : []),
            <Button
              key="settings"
              variant="white"
              size="sm"
              disabled={!workspaceId}
              title={workspaceId ? undefined : `Select a workspace to set automation limits`}
              onClick={() => setSettingsOpen(true)}
            >
              <GearSixIcon size={16} weight="bold" />
              Settings
            </Button>,
            // Templates live behind this button and its sheet, never inline on the page (Patrick,
            // 2026-09-03): a gallery sitting in the empty state read as the page's content.
            <Button key="templates" variant="white" size="sm" onClick={() => setTemplatesOpen(true)}>
              <SquaresFourIcon size={16} weight="bold" />
              Templates
            </Button>,
          ]}
          primaryButtonSlot={
            <Button variant="primary" size="sm" onClick={() => navigate(`/workflows/new`)}>
              <PlusIcon size={16} weight="bold" />
              New workflow
            </Button>
          }
        />
      }
    >
      <Sheet open={templatesOpen} onOpenChange={setTemplatesOpen}>
        <SheetContent
          side="right"
          className="w-full overflow-y-auto sm:max-w-2xl"
          onEscapeKeyDown={(event) => event.preventDefault()}
        >
          <SheetHeader>
            <SheetTitle>Templates</SheetTitle>
          </SheetHeader>
          <div className="px-4 pb-6">
            <TemplateGallery
              showSystem={canSeeEnrolment}
              onPick={pickTemplate}
              onDeployed={afterDeploy}
              gating={templateGating}
            />
          </div>
        </SheetContent>
      </Sheet>

      {settingsOpen && (
        <GlobalSettingsModal
          initial={globalSettings}
          onClose={() => setSettingsOpen(false)}
          onSave={async (next) => {
            if (!workspaceId) return `No workspace selected.`
            const result = await workspaceStore.update(workspaceId, workspaceInputFromSettings(next))
            if (result.error) {
              logError(`Failed to save workspace throttle settings`, result.error)
              return result.error.message.replace(/^\[GraphQL\]\s*/, ``)
            }
            setGlobalSettings(next)
            setSettingsOpen(false)
            notifySuccess(`Automation settings saved`)
            return null
          }}
        />
      )}

      <SimpleTable
        // `undefined` is the table's loading state. Without it the first paint of an empty workspace
        // is "No workflows yet", which is a claim we can't make until the query answers. Locations
        // are waited on too, so a workspace with no local drafts settles its column set before any
        // row paints, rather than a Location column arriving a beat after them.
        data={(result.fetching || locationsResult.fetching) && rows.length === 0 ? undefined : rows}
        columns={columns}
        showColumnOrdering={false}
        hideFooter
        // Clicking anywhere on the row opens the workflow, which is what the hand-rolled table did
        // and what people already expect from the contacts and tasks lists.
        meta={tableMeta}
        emptyState={
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <span className="flex size-10 items-center justify-center rounded-xl bg-surface-muted text-subtle">
              <GitForkIcon size={22} weight="bold" />
            </span>
            <p className="mt-3 text-sm font-medium text-default">No workflows yet</p>
            <Button variant="primary" size="sm" className="mt-4" onClick={() => navigate(`/workflows/new`)}>
              <PlusIcon size={16} weight="bold" />
              New workflow
            </Button>
          </div>
        }
      />
    </PageScroll>
  )
})
